import React from 'react';
import clsx from 'clsx';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { InsightsTrendPoint } from '@/services/facebook-insights.service';

interface Props {
  data: InsightsTrendPoint[];
  /** Which metric to plot. Defaults to spend — most intuitive on a sparkline. */
  metric?: 'spend' | 'impressions' | 'clicks' | 'reach' | 'conversions';
  height?: number;
  className?: string;
}

/**
 * Inline per-campaign trend strip for the campaigns list. Pure SVG, no chart
 * library, designed to slot into a single row without adding much height.
 *
 * Renders:
 *   - A sparkline (filled area)
 *   - Today's value + period delta
 *   - "No data" placeholder when there's no history yet (snapshots cron
 *     hasn't filled in for this campaign yet)
 */
export const CampaignSparkline: React.FC<Props> = ({
  data,
  metric = 'spend',
  height = 28,
  className,
}) => {
  // Need at least 2 data points to draw a meaningful trend. With 0 or 1 we
  // hide the strip entirely — a single number masquerading as a "trend"
  // takes a row of vertical space without actually telling the user
  // anything.
  if (!data || data.length < 2) return null;

  const values = data.map((p) => Number(p[metric]) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const W = 200;
  const H = height;
  const range = max - min || 1;

  // Build the polyline + filled area
  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * (W - 2) + 1;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return [x, y] as const;
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${H} L${points[0][0].toFixed(1)},${H} Z`;

  // Period over period: split into halves
  const half = Math.floor(values.length / 2);
  const recent = values.slice(half).reduce((s, v) => s + v, 0);
  const prior = values.slice(0, half).reduce((s, v) => s + v, 0);
  const delta = prior > 0 ? ((recent - prior) / prior) * 100 : null;

  const total = values.reduce((s, v) => s + v, 0);
  const fmt = metric === 'spend'
    ? total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : total.toLocaleString();

  return (
    <div className={clsx('flex items-center gap-3', className)}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block flex-1 max-w-[240px]">
        <defs>
          <linearGradient id="campaign-spark-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g className="text-primary">
          <path d={areaPath} fill="url(#campaign-spark-grad)" />
          <path d={linePath} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        </g>
      </svg>

      <div className="flex shrink-0 items-baseline gap-1.5 text-2xs">
        <span className="text-text-muted">{data.length}d</span>
        <span className="font-bold tabular-nums text-text-primary">{fmt}</span>
        {delta !== null && (
          <span className={clsx(
            'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold',
            delta > 0.5 ? 'bg-success-soft text-success'
              : delta < -0.5 ? 'bg-danger-soft text-danger'
                : 'bg-surface-alt text-text-muted',
          )}>
            {delta > 0.5 ? <TrendingUp size={9} /> : delta < -0.5 ? <TrendingDown size={9} /> : <Minus size={9} />}
            {delta > 0 ? '+' : ''}{delta.toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
};
